import styled from '@emotion/styled'
import { useState } from 'react'
import {
    PRIMARY_FONT_CSS_VAR,
    SECONDARY_FONT_CSS_VAR,
} from '../../../../context/CustomizationContextProvider'
import {
    inScrollingLayout,
    scaledFontSize,
} from '../../../../style/scrollingLayout'
import { Constants } from '../../../../style/styleConstants'
import { getTypographyRoleStyle } from '../../../../style/typography'
import { ReviewStar } from '../../reviews/ReviewStar'
import { CarouselReview, ReviewTheme } from './types'
import { useClampOverflow } from './useClampOverflow'

const TITLE_CLAMP_LINES = 2
const BODY_CLAMP_LINES = 3

const SLIDE_WIDTH_RATIO = 0.86

// Cap slide width so a wide track shows more cards, not wider quotes.
const MAX_SLIDE_WIDTH = Math.round(
    Constants.CONTENT_BLOCK_DESKTOP_WIDTH * SLIDE_WIDTH_RATIO,
)

const Slide = styled.div({
    flex: `0 0 ${SLIDE_WIDTH_RATIO * 100}%`,
    minWidth: 0,
    scrollSnapAlign: 'start',
    display: 'flex',
    ...inScrollingLayout({ maxWidth: MAX_SLIDE_WIDTH }),
})

const Card = styled.div({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 14,
    padding: '20px 18px',
    boxSizing: 'border-box',
})

const CardStars = styled.div({
    marginBottom: 12,
})

const CardTitle = styled.div<{ expanded: boolean }>(({ expanded }) => ({
    ...getTypographyRoleStyle('featureTitle', {
        fontFamily: `var(${SECONDARY_FONT_CSS_VAR})`,
        fontWeight: 700,
        lineHeight: 1.3,
    }),
    fontSize: scaledFontSize(16),
    marginBottom: 4,
    ...(expanded
        ? {}
        : {
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: TITLE_CLAMP_LINES,
              overflow: 'hidden',
          }),
}))

const CardQuote = styled.blockquote<{ expanded: boolean }>(({ expanded }) => ({
    ...getTypographyRoleStyle('bodyText', {
        fontFamily: `var(${PRIMARY_FONT_CSS_VAR})`,
        lineHeight: 1.55,
    }),
    fontSize: scaledFontSize(14.5),
    margin: '0 0 14px',
    ...(expanded
        ? {}
        : {
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: BODY_CLAMP_LINES,
              overflow: 'hidden',
          }),
}))

const ReadMore = styled.button({
    alignSelf: 'flex-start',
    background: 'none',
    border: 'none',
    padding: 0,
    margin: '0 0 14px',
    cursor: 'pointer',
    ...getTypographyRoleStyle('linkText', {
        fontFamily: `var(${PRIMARY_FONT_CSS_VAR})`,
        fontWeight: 500,
    }),
    fontSize: scaledFontSize(12.5),
    textDecoration: 'underline',
    textUnderlineOffset: 2,
    textDecorationThickness: 'from-font',
    opacity: 0.75,
})

const CardFooter = styled.div({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 'auto',
    paddingTop: 14,
    ...getTypographyRoleStyle('badgeText', {
        fontFamily: `var(${PRIMARY_FONT_CSS_VAR})`,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    }),
    fontSize: scaledFontSize(12),
})

const Verified = styled.span({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
})

const CheckIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
            d="M20 6L9 17l-5-5"
            stroke="currentColor"
            strokeWidth="2.8"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
)

interface ReviewSlideProps {
    review: CarouselReview
    index: number
    count: number
    theme: ReviewTheme
}

export const ReviewSlide: React.FC<ReviewSlideProps> = ({
    review,
    index,
    count,
    theme,
}) => {
    const { accent, baseText, cardSurface, cardBorder, mutedText } = theme
    const [expanded, setExpanded] = useState(false)
    const [titleOverflow, titleRef] = useClampOverflow(TITLE_CLAMP_LINES)
    const [bodyOverflow, bodyRef] = useClampOverflow(BODY_CLAMP_LINES)
    const overflowing = titleOverflow || bodyOverflow

    const reviewerName = review.userName?.trim() || 'Anonymous'

    return (
        <Slide
            role="group"
            aria-roledescription="slide"
            aria-label={`${index + 1} of ${count}`}
        >
            <Card
                style={{
                    background: cardSurface,
                    border: cardBorder,
                    color: baseText,
                }}
            >
                <CardStars>
                    <ReviewStar
                        rating={review.rating}
                        fillColor={accent}
                        starWidth={15}
                    />
                </CardStars>
                {review.title?.trim() ? (
                    <CardTitle ref={titleRef} expanded={expanded}>
                        {review.title.trim()}
                    </CardTitle>
                ) : null}
                <CardQuote ref={bodyRef} expanded={expanded}>
                    {review.text}
                </CardQuote>
                {overflowing ? (
                    <ReadMore
                        style={{ color: mutedText }}
                        onClick={() => setExpanded((value) => !value)}
                        aria-expanded={expanded}
                    >
                        {expanded ? 'Read less' : 'Read more'}
                    </ReadMore>
                ) : null}
                <CardFooter>
                    <span>{reviewerName}</span>
                    <span style={{ color: mutedText }}>&middot;</span>
                    <Verified style={{ color: accent }}>
                        <CheckIcon />
                        Verified
                    </Verified>
                </CardFooter>
            </Card>
        </Slide>
    )
}
